class BabyCostFull extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.ongoingRows = Array.from(this.querySelectorAll('[data-cost-full-ongoing-row]'));
    this.oneTimeItems = Array.from(this.querySelectorAll('[data-cost-full-item]'));
    this.saveButtons = Array.from(this.querySelectorAll('[data-cost-full-save]'));
    this.notice = this.querySelector('[data-cost-full-notice]');
    this.storageKey = this.dataset.storageKey || 'littleNestBabyCostPlanFullV1';
    this.defaultState = this.getState();

    this.addEventListener('input', (event) => {
      const numberInput = event.target.closest('[data-cost-full-price], [data-cost-full-months]');
      if (numberInput && !this.validateInput(numberInput)) return;
      if (!this.validateAll()) return;
      this.clearValidationNotice();
      this.calculate();
    });
    this.addEventListener('change', (event) => {
      const numberInput = event.target.closest('[data-cost-full-price], [data-cost-full-months]');
      if (numberInput && !this.validateInput(numberInput, true)) return;
      const toggle = event.target.closest('[data-cost-full-toggle]');
      if (toggle) this.updateItemState(toggle.closest('[data-cost-full-item]'));
      if (!this.validateAll()) return;
      this.clearValidationNotice();
      this.calculate();
    });
    this.saveButtons.forEach((button) => button.addEventListener('click', () => this.save()));
    this.querySelector('[data-cost-full-reset]')?.addEventListener('click', () => this.reset());

    this.restore();
    this.oneTimeItems.forEach((item) => this.updateItemState(item));
    this.calculate();
  }

  formatMoney(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(value) ? value : 0);
  }

  positiveNumber(input) {
    const value = Number.parseFloat(input?.value || '0');
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  validateInput(input, report = false) {
    if (!input || input.disabled) return true;
    const rawValue = input.value.trim();
    const value = Number(rawValue);
    let message = '';

    if (rawValue && (!Number.isFinite(value) || input.validity.badInput)) {
      message = 'Enter a valid number.';
    } else if (rawValue && input.matches('[data-cost-full-months]')
      && (!Number.isInteger(value) || value < 0 || value > 12)) {
      message = 'Months must be a whole number from 0 to 12.';
    } else if (rawValue && input.matches('[data-cost-full-price]') && value < 0) {
      message = 'Costs must be 0 or greater.';
    }

    input.setCustomValidity(message);
    if (message) {
      input.setAttribute('aria-invalid', 'true');
      this.showNotice(message, true);
      if (report) input.reportValidity();
      return false;
    }

    input.removeAttribute('aria-invalid');
    return true;
  }

  validateAll(report = false) {
    const inputs = Array.from(this.querySelectorAll(
      '[data-cost-full-price]:not(:disabled), [data-cost-full-months]:not(:disabled)',
    ));
    const invalidInput = inputs.find((input) => !this.validateInput(input));
    if (!invalidInput) return true;
    if (report) invalidInput.reportValidity();
    return false;
  }

  clearValidationNotice() {
    if (this.notice?.classList.contains('is-error')) this.showNotice('');
  }

  updateItemState(item) {
    const toggle = item.querySelector('[data-cost-full-toggle]');
    const price = item.querySelector('[data-cost-full-price]');
    const enabled = Boolean(toggle?.checked);
    item.classList.toggle('is-off', !enabled);
    if (price) price.disabled = !enabled;
  }

  calculate() {
    let ongoingTotal = 0;
    let oneTimeTotal = 0;

    this.querySelectorAll('[data-cost-full-category]').forEach((category) => {
      const ongoingRows = Array.from(category.querySelectorAll('[data-cost-full-ongoing-row]'));
      const oneTimeItems = Array.from(category.querySelectorAll('[data-cost-full-item]'));
      let categoryTotal = 0;

      if (ongoingRows.length) {
        categoryTotal = ongoingRows.reduce((sum, row) => {
          const price = this.positiveNumber(row.querySelector('[data-cost-full-price]'));
          const months = this.positiveNumber(row.querySelector('[data-cost-full-months]'));
          return sum + price * months;
        }, 0);
        ongoingTotal += categoryTotal;
      } else if (oneTimeItems.length) {
        categoryTotal = oneTimeItems.reduce((sum, item) => {
          const enabled = item.querySelector('[data-cost-full-toggle]')?.checked;
          return sum + (enabled ? this.positiveNumber(item.querySelector('[data-cost-full-price]')) : 0);
        }, 0);
        oneTimeTotal += categoryTotal;
      }

      const categoryOutput = category.querySelector('[data-cost-full-category-total]');
      if (categoryOutput) categoryOutput.textContent = this.formatMoney(categoryTotal);
    });

    const grandTotal = ongoingTotal + oneTimeTotal;
    this.querySelectorAll('[data-cost-full-ongoing]').forEach((output) => {
      output.textContent = this.formatMoney(ongoingTotal);
    });
    this.querySelectorAll('[data-cost-full-onetime]').forEach((output) => {
      output.textContent = this.formatMoney(oneTimeTotal);
    });
    this.querySelectorAll('[data-cost-full-total]').forEach((output) => {
      output.textContent = this.formatMoney(grandTotal);
    });

    return { ongoingTotal, oneTimeTotal, grandTotal };
  }

  getState() {
    return {
      ongoing: this.ongoingRows.map((row) => ({
        price: this.positiveNumber(row.querySelector('[data-cost-full-price]')),
        months: this.positiveNumber(row.querySelector('[data-cost-full-months]')),
      })),
      oneTime: this.oneTimeItems.map((item) => ({
        enabled: Boolean(item.querySelector('[data-cost-full-toggle]')?.checked),
        price: this.positiveNumber(item.querySelector('[data-cost-full-price]')),
      })),
    };
  }

  applyState(state) {
    if (!state || !Array.isArray(state.ongoing) || !Array.isArray(state.oneTime)) return false;
    if (state.ongoing.length !== this.ongoingRows.length || state.oneTime.length !== this.oneTimeItems.length) return false;

    this.ongoingRows.forEach((row, index) => {
      const saved = state.ongoing[index];
      row.querySelector('[data-cost-full-price]').value = Math.max(0, Number(saved.price) || 0);
      row.querySelector('[data-cost-full-months]').value = Math.min(12, Math.max(0, Math.round(Number(saved.months) || 0)));
    });
    this.oneTimeItems.forEach((item, index) => {
      const saved = state.oneTime[index];
      item.querySelector('[data-cost-full-toggle]').checked = Boolean(saved.enabled);
      item.querySelector('[data-cost-full-price]').value = Math.max(0, Number(saved.price) || 0);
      this.updateItemState(item);
    });
    return true;
  }

  save() {
    if (!this.validateAll(true)) return;
    try {
      const totals = this.calculate();
      localStorage.setItem(this.storageKey, JSON.stringify({
        version: 1,
        savedAt: new Date().toISOString(),
        state: this.getState(),
        totals,
      }));
      this.showNotice('Your plan has been saved on this device.');
    } catch (error) {
      this.showNotice('This browser could not save your plan.', true);
    }
  }

  restore() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      if (saved?.version === 1 && this.applyState(saved.state)) {
        this.showNotice('Your saved plan has been restored.');
      }
    } catch (error) {
      try {
        localStorage.removeItem(this.storageKey);
      } catch (storageError) {
        // Storage may be blocked; the calculator still works without persistence.
      }
    }
  }

  reset() {
    this.applyState(this.defaultState);
    this.querySelectorAll('[aria-invalid="true"]').forEach((input) => {
      input.removeAttribute('aria-invalid');
      input.setCustomValidity('');
    });
    this.calculate();
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      // Keep reset functional even if storage access is blocked.
    }
    this.showNotice('Default costs have been restored.');
  }

  showNotice(message, isError = false) {
    if (!this.notice) return;
    this.notice.textContent = message;
    this.notice.classList.toggle('is-error', isError);
  }
}

if (!customElements.get('baby-cost-full')) {
  customElements.define('baby-cost-full', BabyCostFull);
}
